import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  breakpoints,
  Button,
  Inline,
  LinkButton,
  Radio,
  RadioGroup,
  Stack,
  vars,
} from '@etg/wings';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { m as motion, AnimatePresence } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { WITH_ORDER, WITHOUT_ORDER } from '../utils/constants';
import LoginFormContainer from '../../../common/authentication/containers/LoginFormContainer';
import LoginMessage from '../../../common/authentication/containers/LoginMessage';
import PageSection from '../../order-details/components/common/PageSection';
import { SourceOfLogin } from '../../../common/authentication/containers/models';
import { routes } from '../../../constants/routesAndApisConstants';

const selectionWrapperStyles = css`
  padding: 16px;

  &:first-child {
    border-bottom: solid 1px ${vars.colors.divider};
  }

  @media (min-width: ${breakpoints._970}) {
    width: 50%;

    &:first-child {
      border-bottom: none;
      border-inline-end: solid 1px ${vars.colors.divider};
    }
  }
`;

const contentStyles = css`
  padding: 0;
`;

const notificationStyles = css`
  margin-top: 24px;
`;

const noBookingLinkStyles = css`
  margin: 15px 0 14px;
`;

const motionVariants = {
  parent: {
    enter: { height: 'auto', transition: { type: 'tween', duration: 0.3, when: 'beforeChildren' } },
    exit: { height: 0, transition: { type: 'tween', duration: 0.3, when: 'afterChildren' } },
  },
  child: {
    enter: { opacity: 1, transition: { type: 'tween', duration: 0.3 } },
    exit: { opacity: 0, transition: { type: 'tween', duration: 0.2 } },
  },
};

const Selection = ({ handleNoOrder, clearForm }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectionType, setSelectionType] = useState(null);
  const [notification, setNotification] = useState({ message: null, type: null });

  const handleChange = (e) => {
    setSelectionType(e.target.value);
  };

  return (
    <PageSection heading={{ title: t('ContactUs.SelectionSection.Title') }}>
      <PageSection.Body classNames={{ contentStyles }} dataTestId="selection-page-section">
        <RadioGroup name="selectionType" onChange={handleChange} selectedValue={selectionType}>
          <Inline collapseBelow={breakpoints._970} noWrap spacing={0}>
            <div className={selectionWrapperStyles}>
              <Radio.Label>
                <Inline alignY="start" noWrap spacing={8}>
                  <Radio data-testid="withOrder-radio" id={WITH_ORDER} value={WITH_ORDER} />
                  <Stack spacing={0}>
                    <strong>{t('ContactUs.SelectionSection.RadioButton.WithOrder.Label')}</strong>
                    <div>{t('ContactUs.SelectionSection.RadioButton.WithOrder.Description')}</div>
                  </Stack>
                </Inline>
              </Radio.Label>
              <AnimatePresence>
                {selectionType === WITH_ORDER && (
                  <motion.div
                    animate="enter"
                    exit="exit"
                    initial="exit"
                    variants={motionVariants.parent}
                  >
                    <motion.div variants={motionVariants.child}>
                      <>
                        {notification?.message && notification?.type && (
                          <LoginMessage
                            className={notificationStyles}
                            notification={notification}
                          />
                        )}
                        <LoginFormContainer
                          clearForm={clearForm}
                          handleLoginSuccess={() => history.push(routes.ORDER_DETAILS_CONTACT_US)}
                          setNotification={setNotification}
                          source={SourceOfLogin.CONTACTUS}
                        />
                        <Stack align="center">
                          <LinkButton
                            className={noBookingLinkStyles}
                            data-testid="contactUs-noBookingInfo-button"
                            onClick={handleNoOrder}
                          >
                            {t('ContactUs.SelectionSection.WithOrder.NoBookingInfo.Button')}
                          </LinkButton>
                        </Stack>
                      </>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className={selectionWrapperStyles}>
              <Stack>
                <Radio.Label>
                  <Inline alignY="start" noWrap spacing={8}>
                    <Radio
                      data-testid="withoutOrder-radio"
                      id={WITHOUT_ORDER}
                      value={WITHOUT_ORDER}
                    />
                    <Stack spacing={0}>
                      <strong>
                        {t('ContactUs.SelectionSection.RadioButton.WithoutOrder.Label')}
                      </strong>
                      <div>
                        {t('ContactUs.SelectionSection.RadioButton.WithoutOrder.Description')}
                      </div>
                    </Stack>
                  </Inline>
                </Radio.Label>
                <AnimatePresence>
                  {selectionType === WITHOUT_ORDER && (
                    <motion.div
                      animate="enter"
                      exit="exit"
                      initial="exit"
                      variants={motionVariants.parent}
                    >
                      <motion.div variants={motionVariants.child}>
                        <Button
                          data-testid="contactUs-withoutOrderContinue-button"
                          onClick={handleNoOrder}
                          style={{ width: '100%' }}
                          variant="primary"
                        >
                          {t('ContactUs.SelectionSection.WithoutOrder.Continue.Button')}
                        </Button>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Stack>
            </div>
          </Inline>
        </RadioGroup>
      </PageSection.Body>
    </PageSection>
  );
};

Selection.propTypes = {
  clearForm: PropTypes.func,
  handleNoOrder: PropTypes.func,
};

export default Selection;
